import { type Account, ChainType } from '@polaris/core/models'
import { ecosystemFlagsMapAtom } from '@polaris/core/stores'
import { atom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { atomWithQuery } from 'jotai-tanstack-query'
import type { Avatar } from '@/src/components/accounts/avatar'
import { userAccountsQuery } from '@/src/queries/keys/user'
import { userAtom } from '../user/atoms'

/**
 * Determines if user accounts are synced with DB.
 *
 * We sync accounts locally if the user is a guest user.
 */
/** Is syncing state to local storage (guest users). */
export const isLocalStateModeAtom = atom<boolean>(
  (get) => get(userAtom).id === 'guest',
)

/** Tracks if any wallet is currently connecting to prevent account operations during connection. */
export const isConnectingWalletAtom = atom(false)
/**
 * NOTE: you probably don't need to access this atom directly, instead use walletAccountsAtom or others that include balance or wallet data in stores/accounts/wallets.ts.
 * NOTICE: bump this version when you make changes to the account model or data type for guest users. We should prefer BE data otherwise.
 */
export const localAccountsAtom = atomWithStorage<Account[]>(
  'polaris-accounts-v2',
  [],
)

export const userAccountsAtom = atomWithQuery((get) => {
  const user = get(userAtom)
  const enabled = user.id !== 'guest' // Only query if user is not guest

  return userAccountsQuery({ enabled })
})

/** Readonly atom that derives from local state for guests, or BE for email users. */
export const accountsAtom = atom<Account[]>((get) => {
  const ecosystemFlags = get(ecosystemFlagsMapAtom)
  const localAccount = get(localAccountsAtom)
  const userAccounts = get(userAccountsAtom)

  const accounts = get(isLocalStateModeAtom)
    ? localAccount
    : (userAccounts?.data ?? [])

  return accounts.filter(
    (account) =>
      // Filter by ecosystem flags
      (ecosystemFlags[account.ecosystem] || false) &&
      // Account types supported by web
      (account.type === 'BrowserExtension' ||
        account.type === 'WatchAddress') &&
      // https://linear.app/osmosis/issue/POLFE-1121/remove-bitcoin-connector
      // Allow Bitcoin only for WatchAddress type
      (account.ecosystem !== ChainType.Bitcoin ||
        account.type === 'WatchAddress'),
  )
})

/** NOTE: this is temporary until we store avatars in the user table. */
export const preferredAvatarAtom = atomWithStorage<Avatar | 'not-set'>(
  'avatar',
  'not-set',
)

export const accountsAddressesAtom = atom((get) => {
  const accounts: Account[] = get(accountsAtom)

  return accounts
    .filter((account) => account.account_status !== 'ignored')
    .map((a) => a.address)
})

export const ignoredAddressesAtom = atom((get) => {
  const accounts = get(accountsAtom)
  return accounts
    .filter((account) => account.account_status === 'ignored')
    .map((account) => account.address)
})
